import React, { FC, useEffect, useRef, useState } from 'react'
import AppPageComponent from '@components/page/appPage.component'
import PageHeaderComponent from '@components/page/pageHeader.component'
import PageBodyComponent from '@components/page/pageBody.component'
import { useTheme } from '@config/theme.config'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '@store/store'
import {
  fetchAllAreas,
  selectAllAreas,
  selectAreaLoading,
} from '@store/slices/area.slice'
import { getSelectButtonClass } from '../tools/style.tools'
import { Button, FormControl, InputGroup } from 'react-bootstrap'
import { fetchPostsByAreaShortcut } from '@store/thunk/regionalPost.thunk'
import {
  RegionalPost,
  selectAllRegionalPosts,
  selectRegionalPostsLoading,
} from '@store/slices/regionalPost.slice'
import { PostBox } from '@components/regions/PostBox.component'
import { SortOrder } from '@store/slices/generic.slice'
import ReactSelect from 'react-select'
import { reactSelectDarkmode } from '@config/slate.config'
import { sortByDatetimeKey } from '../tools/time.tools'

const RegionsPage: React.FC = () => {
  const { theme } = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const areas = useSelector(selectAllAreas)
  const areasLoading = useSelector(selectAreaLoading)
  const [selectedAreaShortcut, setSelectedAreaShortcut] = useState<
    string | null
  >(null)
  const postsLoading = useSelector(selectRegionalPostsLoading)
  const posts = useSelector(selectAllRegionalPosts)
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESCENDING)
  const [filterString, setFilterString] = useState<string>('')

  const [sortedFilteredPosts, setSortedFilteredPosts] = useState<
    RegionalPost[]
  >([])

  useEffect(() => {
    if (areas.length == 0 && !areasLoading) {
      dispatch(fetchAllAreas())
    }
  }, [])

  useEffect(() => {
    if (areas.length != 0 && !areasLoading) {
      setAreaFilter(null)
    }
  }, [areas])

  useEffect(() => {
    if (posts && posts.length > 0) {
      const postsCopy = [...posts]
      setSortedFilteredPosts(
        filterAndSortPosts(postsCopy, filterString, sortOrder)
      )
    }
  }, [posts, sortOrder, filterString])

  const setAreaFilter = (areaShortcut: string | null) => {
    setSelectedAreaShortcut(areaShortcut)
    setSortedFilteredPosts([])
    setFilterString('')
    dispatch(
      fetchPostsByAreaShortcut({
        areaShortcut: areaShortcut,
      })
    )
  }

  const filterAndSortPosts = (
    posts: RegionalPost[],
    filterString: string,
    sortOrder: SortOrder
  ) => {
    return posts
      .filter((post) => {
        for (const tag of post.tags) {
          if (tag.slug?.includes(filterString)) {
            return true
          }
        }
        return (
          post.title?.includes(filterString) ||
          post.description?.includes(filterString) ||
          post.author?.includes(filterString)
        )
      })
      .sort((a, b) => {
        if (sortOrder == SortOrder.ASCENDING) {
          return sortByDatetimeKey(b, a, 'publishTime')
        }
        return sortByDatetimeKey(a, b, 'publishTime')
      })
  }

  const handleFilterChange = (filterString: string) => {
    setFilterString(filterString)
  }

  const handleSortChange = (sortOrder: SortOrder) => {
    setSortOrder(sortOrder)
  }

  const AreaFilter = () => {
    const availableAreas = areas
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter(
        (model, index, self) =>
          index === self.findIndex((t) => t.name === model.name) // Filter out duplicate areas
      )
    return (
      <div className="my-3 d-flex flex-row w-50">
        <Button
          disabled={selectedAreaShortcut == null}
          variant={'outline-secondary'}
          className={`me-2 ${getSelectButtonClass(selectedAreaShortcut == null, theme)}`}
          onClick={() => setAreaFilter(null)}
          style={{ minWidth: 'fit-content' }}
        >
          Vše
        </Button>
        {availableAreas.map((area) => (
          <Button
            key={area.shortcut}
            disabled={selectedAreaShortcut == area.shortcut}
            variant={'outline-secondary'}
            className={`me-1 ${getSelectButtonClass(selectedAreaShortcut == area.shortcut, theme)}`}
            onClick={() => setAreaFilter(area.shortcut)}
            style={{ minWidth: 'fit-content' }}
          >
            {area.name}
          </Button>
        ))}
      </div>
    )
  }

  const PostsArea: FC<object> = () => {
    return (
      <>
        {postsLoading && <div className={''}>Načítání...</div>}
        {!postsLoading && !!posts && (
          <div className={'mt-2'}>
            {!postsLoading && !!posts && (
              <div>Nalezeno {sortedFilteredPosts.length} článků</div>
            )}
            {sortedFilteredPosts.map((post, id) => (
              <PostBox key={id} post={post} />
            ))}
          </div>
        )}
      </>
    )
  }

  return (
    <AppPageComponent>
      <PageHeaderComponent>
        <div className={'d-flex flex-row justify-content-between'}>
          <AreaFilter />
          <div className="my-3 d-flex flex-row w-50 justify-content-end">
            <InputGroup className="d-flex flex-row justify-content-end">
              <OrderSelect
                onSortChange={setSortOrder}
                defaultOrder={sortOrder}
              />
              <FilterForm
                onFilterChange={handleFilterChange}
                filterString={filterString}
              />
            </InputGroup>
          </div>
        </div>
      </PageHeaderComponent>
      <PageBodyComponent>
        <PostsArea />
      </PageBodyComponent>
    </AppPageComponent>
  )
}
export default RegionsPage

const OrderSelect: FC<{
  onSortChange: (sortOrder: SortOrder) => void
  defaultOrder: SortOrder
}> = ({ onSortChange, defaultOrder }) => {
  const { theme } = useTheme()
  const sortingOptions = [
    {
      value: SortOrder.DESCENDING,
      label: 'Od nejnovější',
    },
    {
      value: SortOrder.ASCENDING,
      label: 'Od nejstarší',
    },
  ]

  return (
    <ReactSelect
      defaultValue={sortingOptions.find(
        (option) => option.value === defaultOrder
      )}
      options={sortingOptions}
      className={'mx-2'}
      styles={reactSelectDarkmode(theme)}
      onChange={(option) => (option ? onSortChange(option.value) : null)}
    />
  )
}

const FilterForm: FC<{
  onFilterChange: (search: string) => void
  filterString: string
}> = ({ onFilterChange, filterString }) => {
  const filterInputRef = useRef(null)
  const [searchValue, setSearchValue] = useState<string>(
    filterString
  )

  useEffect(() => {
    //@ts-ignore
    filterInputRef.current?.focus()
  }, [searchValue])

  useEffect(() => {
    setSearchValue(filterString)
  }, [filterString])

  const handleFilterChange = (e: React.ChangeEvent) => {
    e.preventDefault()
    //@ts-ignore
    const searchValue = e.target.value
    setSearchValue(searchValue)
    onFilterChange(searchValue)
  }

  return (
    <FormControl
      placeholder="Vyhledávaní podle klíčového slova, ID, Tagu nebo autora"
      ref={filterInputRef}
      onChange={handleFilterChange}
      style={{ maxWidth: '425px' }}
      value={searchValue}
    />
  )
}
